import * as amplitude from "@amplitude/analytics-browser";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { PickupLocation } from "@redotech/redo-model/return-flow";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { DateInput } from "@redotech/redo-web/date-picker";
import PickupTruckIcon from "@redotech/redo-web/icon/pickup-truck.svg";
import { Modal, ModalSize } from "@redotech/redo-web/modal";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { InputLines, TextInput } from "@redotech/redo-web/text-input";
import {
  InputProvider,
  groupInput,
  input,
  optionalInput,
} from "@redotech/ui/form";
import { optionalEqual } from "@redotech/util/equal";
import {
  dateToCurrentPlainDate,
  plainDateToCurrentDate,
} from "@redotech/util/temporal";
import * as classNames from "classnames";
import { memo, useContext, useEffect, useMemo } from "react";
import { CurrencyContext } from "../../../app/currency";
import { PickupContext } from "../../../contexts/PickupContext";
import * as review from "../review.module.css";

interface PickupLocationOption {
  value: PickupLocation;
  label: string;
  detail: string;
}

// TODO: Change on 03/18/2025
const FEDERAL_HOLIDAYS = [
  { month: 1, day: 1 },
  { month: 1, day: 20 },
  { month: 2, day: 17 },
  { month: 5, day: 27 },
  { month: 6, day: 19 },
  { month: 7, day: 4 },
  { month: 9, day: 2 },
  { month: 10, day: 14 },
  { month: 11, day: 11 },
  { month: 11, day: 28 },
  { month: 12, day: 25 },
];

export const pickupLocationOptions: Map<PickupLocation, PickupLocationOption> =
  new Map(
    [
      {
        value: PickupLocation.FRONT_DOOR,
        label: "Front Door",
        detail: "from the front door",
      },
      {
        value: PickupLocation.BACK_DOOR,
        label: "Back Door",
        detail: "from the back door",
      },
      {
        value: PickupLocation.SIDE_DOOR,
        label: "Side Door",
        detail: "from the side door",
      },
      { value: PickupLocation.OTHER, label: "Other", detail: "in another way" },
    ].map((option) => [option.value, option]),
  );

const pickupDetailsForm = groupInput(
  {
    location: input<PickupLocation | undefined>({
      validator: (location) => (location ? [] : ["Required"]),
    }),
    date: input<Temporal.PlainDate | undefined>({
      equal: optionalEqual((a, b) => a.equals(b)),
      validator: (date) => (date !== undefined ? [] : ["Required"]),
    }),
    specialInstructions: input<string>(),
  },
  {
    validator: ({ location, specialInstructions }) =>
      location !== PickupLocation.OTHER || specialInstructions
        ? []
        : ["Instructions required"],
  },
);

type PickupDetails = InputProvider.Value<typeof pickupDetailsForm>;

export const pickupForm = groupInput({
  finalized: input<boolean>({
    validator: (value) => (value ? [] : ["Required"]),
  }),
  pickup: optionalInput(
    pickupDetailsForm,
    (): PickupDetails => ({
      location: undefined,
      date: undefined,
      specialInstructions: "",
    }),
  ),
});

export type PickupForm = InputProvider.Form<typeof pickupForm>;

const getValidDate = (date: Temporal.PlainDate, days: number) => {
  date = date.add({ days });

  while (!isValidDate(date)) {
    date = date.add({ days: 1 });
  }

  return date;
};

// FedEx does not allow pickups on weekends or holidays
const isValidDate = (date: Temporal.PlainDate) => {
  return (
    date.dayOfWeek !== 7 && date.dayOfWeek !== 6 && !isFederalHoliday(date)
  );
};

const isFederalHoliday = (date: Temporal.PlainDate) => {
  return FEDERAL_HOLIDAYS.some(
    (holiday) => date.day === holiday.day && date.month === holiday.month,
  );
};

export const PickupFormCard = memo(
  ({
    upsell,
    labelDeducted,
    setLoading,
    isReviewStep,
    isMerchantDashboard,
  }: {
    upsell?: number | undefined;
    labelDeducted?: boolean;
    setLoading?: (loading?: boolean) => void;
    isReviewStep?: boolean;
    isMerchantDashboard?: boolean;
  }) => {
    const { formatCurrency } = useContext(CurrencyContext);
    const today = useMemo(() => Temporal.Now.plainDateISO(), []);

    const minDate = getValidDate(today, 1);
    // FedEx can be scheduled up to 14 days in advance
    const maxDate = getValidDate(today, 14);

    const { input } = useContext(PickupContext);

    const { finalized, pickup } = input.inputs;
    const { date, location, specialInstructions } = pickup.input?.inputs ?? {};

    useEffect(() => {
      if (pickup.value?.date === undefined) {
        date.setValue(minDate);
      }
      if (pickup.value?.location === undefined) {
        location.setValue(PickupLocation.FRONT_DOOR);
      }
    }, []);

    return (
      <>
        {!isMerchantDashboard && (
          <div className={review.summaryCardItem}>
            <div className={review.summaryText}>
              Your package will be picked up from the shipping address on{" "}
              {isReviewStep ? "the next page." : "this page."}{" "}
              {labelDeducted ? (
                <>
                  An additional charge of{" "}
                  <text className={review.price}>{formatCurrency(upsell)}</text>{" "}
                  will be deducted from your refund.
                </>
              ) : (
                <>
                  You will be charged an additional charge of{" "}
                  <text className={review.price}>{formatCurrency(upsell)}</text>
                  .
                </>
              )}
            </div>
          </div>
        )}
        <div className={classNames(review.summaryCardItem, review.left)}>
          <div className={review.summaryTextBold}>Pickup details:</div>
        </div>
        <div className={classNames(review.summaryCardItem, review.center)}>
          <DateInput
            value={date.value ? plainDateToCurrentDate(date.value) : undefined}
            valueChange={(value) => {
              const plainDate = dateToCurrentPlainDate(value);
              if (value && isValidDate(plainDate)) {
                date.setValue(plainDate);
              }
            }}
            minDate={plainDateToCurrentDate(minDate)}
            maxDate={plainDateToCurrentDate(maxDate)}
            minDetail="year"
            includeYearNav={false}
            filterDate={({ date }) => {
              return isValidDate(dateToCurrentPlainDate(date));
            }}
          />
        </div>
        <div className={review.summaryCardItem}>
          <div className={review.summaryDisclaimer}>
            Pickup will happen during the first delivery day on or after the
            selected date.
          </div>
        </div>
        <SelectDropdown
          options={[...pickupLocationOptions.keys()]}
          value={location.value}
          valueChange={(value) => location.setValue(value)}
          className={review.pickupLocationDropdown}
        >
          {(option) => pickupLocationOptions.get(option).label}
        </SelectDropdown>
        <div className={review.summaryCardItem}>
          <div className={review.summaryDisclaimer}>
            If {isMerchantDashboard ? "the customer lives" : "you live"} in a
            multi-story building, select an option which is ground level and
            secure.
          </div>
        </div>
        {location.value === PickupLocation.OTHER && (
          <TextInput
            placeholder="Special instructions"
            lines={InputLines.MULTI}
            value={specialInstructions.value}
            onChange={(val) => specialInstructions.setValue(val)}
            maxLength={60}
            fullwidth={true}
          />
        )}
        {!isMerchantDashboard && (
          <Button
            theme={ButtonTheme.BRAND}
            disabled={!!pickup.allErrors.length}
            onClick={() => {
              if (!isMerchantDashboard) {
                amplitude.logEvent("pickup-saved", {
                  date: date.toString(),
                  location: location.value,
                  specialInstructions: specialInstructions.value,
                });
              }
              setLoading(true);
              finalized.setValue(true);
            }}
          >
            Add package pickup
          </Button>
        )}
      </>
    );
  },
);

const BannerUpsale = memo(({ upsell }: { upsell?: number }) => {
  const size = useScreenType();
  const { formatCurrency } = useContext(CurrencyContext);
  const { input } = useContext(PickupContext);

  useEffect(() => {
    amplitude.logEvent("pickup-available", {
      upsell: upsell,
    });
  }, []);

  return (
    <>
      <div
        className={
          size === ScreenType.MOBILE
            ? review.summaryCardItem
            : review.summaryCardItemHorizonal
        }
      >
        <div className={review.summaryHeader}>
          Skip the hassle. Let us take it from here.
        </div>
        <div className={review.summaryText}>
          Pack up your return and leave it at your door. We can handle the rest
          for only{" "}
          <text className={review.price}>{formatCurrency(upsell)}</text>.
        </div>
      </div>
      <div className={review.summaryCardItem}>
        <Button
          theme={ButtonTheme.BRAND}
          className={review.pickupButton}
          onClick={() => {
            amplitude.logEvent("pickup-add");
            input.setValue({
              finalized: false,
              pickup: {
                location: undefined,
                date: undefined,
                specialInstructions: "",
              },
            });
          }}
        >
          Add package pickup
        </Button>
      </div>
    </>
  );
});

export const PickupCard = memo(
  ({
    labelDeducted,
    upsell,
    setLoading,
  }: {
    labelDeducted: boolean;
    upsell?: number;
    setLoading: (loading: boolean) => void;
  }) => {
    const { input } = useContext(PickupContext);

    const { finalized, pickup } = input.inputs;
    const { specialInstructions } = pickup.input?.inputs ?? {};

    const size = useScreenType();

    useEffect(() => {
      if (pickup.value?.location !== PickupLocation.OTHER) {
        specialInstructions?.setValue("");
      }
    }, [pickup.value?.location]);

    if (!pickup.value && finalized.value) {
      return null;
    }

    return (
      <div
        className={
          size === ScreenType.MOBILE
            ? review.summaryCardPickup
            : review.summaryCardHorizonal
        }
      >
        <div className={review.summaryCardItem}>
          <PickupTruckIcon />
        </div>
        {pickup.value && finalized.value && (
          <>
            <div
              className={
                size === ScreenType.MOBILE
                  ? review.summaryCardItem
                  : review.summaryCardItemHorizonal
              }
            >
              <div className={review.summaryTextBold}>
                Package pickup is added!
              </div>
              <div className={review.summaryText}>
                Your package will be picked up from the shipping address on this
                page on{" "}
                {plainDateToCurrentDate(pickup.value.date).toLocaleDateString()}{" "}
                {pickupLocationOptions.get(pickup.value.location).detail}.
              </div>
            </div>
          </>
        )}
        {!pickup.value && <BannerUpsale upsell={upsell} />}
        {pickup.value &&
          !finalized.value &&
          {
            [ScreenType.DESKTOP]: (
              <Modal
                open={true}
                onClose={() =>
                  input.setValue({ finalized: false, pickup: undefined })
                }
                title="Add details to your package pickup"
                size={ModalSize.MEDIUM}
              >
                <PickupFormCard
                  upsell={upsell}
                  labelDeducted={labelDeducted}
                  setLoading={setLoading}
                />
              </Modal>
            ),
            [ScreenType.MOBILE]: (
              <>
                <div className={review.summaryHeader}>
                  Add details to your package pickup
                </div>
                <PickupFormCard
                  upsell={upsell}
                  labelDeducted={labelDeducted}
                  setLoading={setLoading}
                />
                <Button
                  onClick={() => {
                    amplitude.logEvent("pickup-cancel");
                    input.setValue({ finalized: false, pickup: undefined });
                  }}
                >
                  Cancel
                </Button>
              </>
            ),
          }[size]}
      </div>
    );
  },
);
